<script setup>
defineProps({
  sticky: { type: Boolean, default: false },
})
</script>

<template>
  <div v-if="sticky" class="toolbar-sticky">
    <div class="page-header"><slot /></div>
  </div>
  <div v-else class="page-header"><slot /></div>
</template>

<style scoped>
/*
 * Toolbar shell + atoms.
 *
 * `.page-header`        — the dark rounded bar (the toolbar surface itself).
 * `.toolbar-sticky`     — outer sticky wrapper, opt in via the `sticky` prop.
 *
 * Slot atoms (consumers drop these into the default slot):
 *   .toolbar-spacer, .header-tabs, .tab-btn, .toolbar-search-box,
 *   .btn-header, .btn-create, .btn-danger, .btn-back
 *
 * Slot content carries the consumer's scope, not ours, so we reach it with
 * :deep(). The :deep reach is bounded by the .page-header root — stray atoms
 * elsewhere in the app won't accidentally pick up toolbar styling.
 *
 * Every control stands --toolbar-control-h, declared once below and inherited
 * by the slot. Left to their padding, atoms size to their contents and drift
 * apart — a .tab-btn carrying a count badge stands taller than a bare one. The
 * atoms' vertical padding is therefore 0: height is what sets height, and
 * align-items: center places the contents.
 */

.toolbar-sticky {
  position: sticky;
  top: -1px;
  z-index: 10;
  background: var(--surface-page);
  margin: 0 -2rem;
  padding: 0 2rem 1.25rem;
}

/* Fixed 66px bar so every toolbar is the same height regardless of what it
 * carries — this is the height a two-line <ToolbarHeading/> needs. box-sizing:
 * border-box folds the padding into the 66px. It's a min-height (not height) so
 * a wrapping row on a narrow viewport grows the bar instead of clipping; content
 * shorter than the bar simply centers. `slim` opts a control-only toolbar down
 * to 52px. */
.page-header {
  --toolbar-control-h: 2.5rem;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  min-height: 66px;
  padding: 0.5rem 0.75rem;
  background: var(--surface-card);
  border-radius: var(--radius-xl);
  flex-wrap: wrap;
  gap: 0.5rem;
  flex-shrink: 0;
}

/* There's no global border-box reset, so anything given a fixed height needs
   this or its padding lands on top of --toolbar-control-h. */
:deep(.tab-btn),
:deep(.btn-header),
:deep(.btn-create),
:deep(.btn-danger),
:deep(.btn-back),
:deep(.dropdown-trigger),
:deep(.toolbar-search-box) {
  box-sizing: border-box;
  height: var(--toolbar-control-h);
}

/* Controls keep their natural height and center within the bar rather than
 * stretching to fill it. */
:deep(.toolbar-spacer) {
  flex: 1;
}

/* Dropdown triggers live in the slot and default to align-self: stretch with
 * 0.5rem vertical padding (global dropdown-trigger.css); center them and let
 * the shared height rule size them, like the other atoms — scoped here so
 * non-toolbar dropdowns keep their own sizing. */
:deep(.dropdown-trigger) {
  align-self: center;
  padding: 0 0.875rem;
}

/* Tab group — visually grouped related tabs. Tight gap separates tabs in the
 * same group; the toolbar's own gap separates groups. */
:deep(.header-tabs) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.125rem;
}

/* Individual tab. Shares rhythm with .btn-header / .dropdown-trigger. */
:deep(.tab-btn) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-lg);
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: var(--transition-fast);
  white-space: nowrap;
}

:deep(.tab-btn:hover:not(.active)) {
  background: var(--surface-hover);
  color: white;
}

:deep(.tab-btn.active) {
  background: var(--accent-soft);
  color: white;
  font-weight: 600;
}

:deep(.tab-btn.active svg) {
  color: var(--accent-text);
}

/* Plain-<input> search chip (the admin/list pages). The autocompleting,
   card-look variant is the ToolbarQueryInput component. */
:deep(.toolbar-search-box) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.375rem;
  background: var(--surface-overlay);
  border-radius: var(--radius-md);
  padding: 0 0.625rem;
  min-width: 0;
  flex: 1;
  max-width: 280px;
}

:deep(.toolbar-search-box .search-icon) {
  color: var(--text-disabled);
  font-size: 0.75rem;
  flex-shrink: 0;
}

:deep(.toolbar-search-box .search-input) {
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-family: inherit;
  width: 100%;
  min-width: 0;
}

:deep(.toolbar-search-box .search-input::placeholder) {
  color: var(--text-disabled);
}

:deep(.toolbar-search-box .search-clear) {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-xs);
  color: var(--text-faint);
  cursor: pointer;
  font-size: 0.6875rem;
  flex-shrink: 0;
  transition: var(--transition-fast);
}

:deep(.toolbar-search-box .search-clear:hover) {
  color: var(--text-primary);
  background: var(--surface-overlay-3);
}

/* Header action buttons */
:deep(.btn-header) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.5rem;
  padding: 0 1rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-lg);
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

:deep(.btn-header:hover) {
  background: var(--surface-hover);
  color: white;
}

:deep(.btn-create) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  background: var(--accent-soft-3);
  border: none;
  border-radius: var(--radius-lg);
  color: var(--accent-text);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
}

:deep(.btn-create:hover) {
  background: var(--accent-soft-4);
  color: white;
}

/* Destructive action (delete a source/group). */
:deep(.btn-danger) {
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  background: rgba(239, 68, 68, 0.15);
  border: none;
  border-radius: var(--radius-lg);
  color: var(--color-error-text);
  font-size: 0.8125rem;
  font-weight: 500;
  font-family: var(--font-heading);
  cursor: pointer;
  transition: var(--transition);
  white-space: nowrap;
}

:deep(.btn-danger:hover:not(:disabled)) {
  background: rgba(239, 68, 68, 0.28);
  color: white;
}

:deep(.btn-danger:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Square icon button that leads a detail-page toolbar (back to the list). The
   shared height rule makes it square. */
:deep(.btn-back) {
  display: inline-flex;
  align-items: center;
  align-self: center;
  justify-content: center;
  width: var(--toolbar-control-h);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: 1.1rem;
  flex-shrink: 0;
  transition: var(--transition);
}

:deep(.btn-back:hover) {
  background: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
